import { ListNsnCandidatesResponse, NsnCandidate } from '@/api/generatedApi';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { NsnCandidateSummary } from './NsnCandidateSummary';

export interface NsnPickerChoice {
  candidates: NsnCandidate[];
  partNumberOnly: boolean;
}

interface NsnCandidatePickerProperties {
  partNumber: string;
  listing: ListNsnCandidatesResponse;
  isPending: boolean;
  onConfirm: (choice: NsnPickerChoice) => void;
}

const PickerPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const CandidateOption = styled(FormControlLabel)({
  alignItems: 'flex-start',
  marginTop: '4px',
  '& .MuiCheckbox-root': { paddingTop: '2px' },
});

const PickerFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const createLabel = (count: number): string => (count > 1 ? `Create ${count} reports` : 'Create report');

const toggle = (selected: string[], niin: string): string[] =>
  selected.includes(niin) ? selected.filter(value => value !== niin) : [...selected, niin];

export const NsnCandidatePicker = ({ partNumber, listing, isPending, onConfirm }: NsnCandidatePickerProperties) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [partNumberOnly, setPartNumberOnly] = useState(false);
  const selectedCount = selected.length + (partNumberOnly ? 1 : 0);
  const { nsnCandidates, sharedCharacteristics } = listing;

  return (
    <PickerPanel data-testid='nsn-candidate-picker'>
      <Typography variant='body2'>
        {partNumber} matches {nsnCandidates.length} NSNs in FLIS. Select one or more:
      </Typography>
      <FormGroup>
        {nsnCandidates.map(candidate => (
          <CandidateOption
            key={candidate.niin}
            control={
              <Checkbox
                size='small'
                checked={selected.includes(candidate.niin)}
                onChange={() => setSelected(current => toggle(current, candidate.niin))}
              />
            }
            label={<NsnCandidateSummary candidate={candidate} />}
          />
        ))}
        <CandidateOption
          control={
            <Checkbox size='small' checked={partNumberOnly} onChange={() => setPartNumberOnly(current => !current)} />
          }
          label={<Typography variant='body2'>Search as a part number only (skip FLIS)</Typography>}
        />
      </FormGroup>
      <PickerFooter>
        <Typography variant='caption' color='text.secondary'>
          {sharedCharacteristics.length > 0 &&
            `Shared: ${sharedCharacteristics.map(characteristic => characteristic.value).join(' · ')}`}
        </Typography>
        <Button
          variant='contained'
          disabled={selectedCount === 0 || isPending}
          onClick={() =>
            onConfirm({
              candidates: nsnCandidates.filter(candidate => selected.includes(candidate.niin)),
              partNumberOnly,
            })
          }
          data-testid='create-report-from-choice-button'
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {createLabel(selectedCount)}
        </Button>
      </PickerFooter>
    </PickerPanel>
  );
};
